import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@istari-frontend/ui";
import { CircleAlertIcon, CircleCheckIcon, Loader2Icon, TriangleAlertIcon } from "lucide-react";
import { useId, useMemo, useState } from "react";

import type { EntityLink } from "~/ai-chat/model/entity-link";
import {
  extensionOf,
  isValidSaveResourceFileName,
  SAVE_RESOURCE_ALLOWED_EXTENSIONS,
  type SaveResourceAction,
} from "~/ai-chat/model/save-resource-action";
import { ChatArtifactCard } from "~/ai-chat/ui/chat-artifact-card";
import { ChatEntityChip } from "~/ai-chat/ui/chat-entity-chip";
import { cn } from "~/utils/cn";
import { formatBytes } from "~/utils/format-bytes";
import { formatInfosecLevelName } from "~/utils/infosec";

export interface InfosecLevelOption {
  id: string;
  name: string;
  displayColor: string;
}

export type SaveResourceCardState =
  | { status: "ready" }
  | { status: "saving" }
  | { status: "saved"; link: EntityLink; warning?: string }
  | { status: "error"; message: string };

interface ChatSaveResourceCardProps {
  action: SaveResourceAction;
  state: SaveResourceCardState;
  // undefined = the platform infosec setting hasn't resolved yet; saving stays
  // blocked so an infosec-required deployment can't slip through the gap.
  infosecEnabled: boolean | undefined;
  infosecLevels: InfosecLevelOption[];
  onSave: (action: SaveResourceAction, infosecLevelId?: string) => void;
}

export function ChatSaveResourceCard({
  action,
  state,
  infosecEnabled,
  infosecLevels,
  onSave,
}: ChatSaveResourceCardProps) {
  const fileNameId = useId();
  const displayNameId = useId();
  const infosecLevelSelectId = useId();
  const [fileName, setFileName] = useState(action.fileName);
  const [displayName, setDisplayName] = useState(action.displayName);
  const [infosecLevelId, setInfosecLevelId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const lines = useMemo(() => action.content.split("\n"), [action.content]);
  const byteSize = useMemo(() => new Blob([action.content]).size, [action.content]);
  const isTruncated = lines.length > PREVIEW_LINE_COUNT;
  const preview = expanded || !isTruncated ? action.content : lines.slice(0, PREVIEW_LINE_COUNT).join("\n");

  const trimmedFileName = fileName.trim();
  const trimmedDisplayName = displayName.trim();
  const isFileNameValid = isValidSaveResourceFileName(trimmedFileName);
  const isMissingInfosecLevel = infosecEnabled === true && infosecLevelId === null;
  const canSave =
    isFileNameValid && trimmedDisplayName.length > 0 && infosecEnabled !== undefined && !isMissingInfosecLevel;
  const isLocked = state.status === "saving" || state.status === "saved";

  const requestSave = () => {
    if (!canSave) return;
    onSave({ ...action, fileName: trimmedFileName, displayName: trimmedDisplayName }, infosecLevelId ?? undefined);
  };

  return (
    <div className="border-border bg-card my-1 w-full rounded-lg border p-2.5 first:mt-0 last:mb-0">
      {state.status === "saved" ? (
        <ChatArtifactCard
          name={trimmedFileName}
          kind={extensionOf(trimmedFileName)}
          meta={formatBytes(byteSize)}
          className="border-0 bg-transparent p-0"
        />
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor={fileNameId} className="text-xs">
              Resource name
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id={fileNameId}
                value={fileName}
                onChange={(event) => setFileName(event.target.value)}
                disabled={isLocked}
                aria-invalid={!isFileNameValid}
                className="h-7 flex-1 text-xs"
              />
              <span className="text-muted-foreground bg-background/60 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase">
                {extensionOf(trimmedFileName) || "—"}
              </span>
              <span className="text-muted-foreground shrink-0 text-[11px]">{formatBytes(byteSize)}</span>
            </div>
            {!isFileNameValid && (
              <p className="text-destructive text-xs">
                Use a plain file name ending in one of: {SAVE_RESOURCE_ALLOWED_EXTENSIONS.join(", ")}.
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor={displayNameId} className="text-xs">
              Display name
            </Label>
            <Input
              id={displayNameId}
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              disabled={isLocked}
              aria-invalid={trimmedDisplayName.length === 0}
              className="h-7 text-xs"
            />
          </div>
          {infosecEnabled && (
            <div className="flex flex-col gap-1">
              <Label htmlFor={infosecLevelSelectId} className="text-xs">
                Infosec level <span className="text-destructive">*</span>
              </Label>
              <Select
                required
                value={infosecLevelId ?? ""}
                onValueChange={(value) => setInfosecLevelId(value || null)}
                disabled={isLocked}
              >
                <SelectTrigger
                  id={infosecLevelSelectId}
                  className={cn(
                    "h-7 w-full text-xs",
                    isMissingInfosecLevel && "border-destructive ring-destructive/20",
                  )}
                  aria-invalid={isMissingInfosecLevel}
                >
                  <SelectValue placeholder="Select level…" />
                </SelectTrigger>
                <SelectContent>
                  {infosecLevels.map((level) => (
                    <SelectItem key={level.id} value={level.id}>
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full" style={{ backgroundColor: level.displayColor }} />
                        {formatInfosecLevelName(level.name)}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}
      <pre className="bg-background/60 mt-2 max-h-48 max-w-full overflow-auto rounded-md p-2 font-mono text-xs [overflow-wrap:anywhere] whitespace-pre-wrap">
        {preview}
      </pre>
      {isTruncated ? (
        <Button
          type="button"
          variant="link"
          size="2xs"
          onClick={() => setExpanded((previous) => !previous)}
          className="text-muted-foreground h-auto px-0"
        >
          {expanded ? "Show less" : `Show all ${lines.length} lines`}
        </Button>
      ) : null}
      <div className="mt-2">
        <SaveResourceCardFooter
          state={state}
          fileName={trimmedFileName}
          canSave={canSave}
          onRequestSave={requestSave}
        />
      </div>
    </div>
  );
}

const PREVIEW_LINE_COUNT = 10;

interface SaveResourceCardFooterProps {
  state: SaveResourceCardState;
  fileName: string;
  canSave: boolean;
  onRequestSave: () => void;
}

function SaveResourceCardFooter({ state, fileName, canSave, onRequestSave }: SaveResourceCardFooterProps) {
  switch (state.status) {
    case "ready":
      return (
        <Button type="button" size="sm" onClick={onRequestSave} disabled={!canSave}>
          Save
        </Button>
      );
    case "saving":
      return (
        <Button type="button" size="sm" disabled>
          <Loader2Icon className="size-3.5 motion-safe:animate-spin" aria-hidden="true" />
          Saving…
        </Button>
      );
    case "saved":
      return (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <CircleCheckIcon className="text-success size-3.5 shrink-0" aria-hidden="true" />
            <span className="text-muted-foreground text-xs">Saved as new resource</span>
            <ChatEntityChip link={state.link}>{fileName}</ChatEntityChip>
          </div>
          {state.warning ? (
            <div className="flex items-center gap-2">
              <TriangleAlertIcon className="text-warning size-3.5 shrink-0" aria-hidden="true" />
              <span className="text-muted-foreground min-w-0 flex-1 text-xs">{state.warning}</span>
            </div>
          ) : null}
        </div>
      );
    case "error":
      return (
        <div className="flex items-center gap-2">
          <CircleAlertIcon className="text-destructive size-3.5 shrink-0" aria-hidden="true" />
          <span className="text-destructive min-w-0 flex-1 text-xs">{state.message}</span>
          <Button type="button" variant="outline" size="sm" onClick={onRequestSave} disabled={!canSave}>
            Try again
          </Button>
        </div>
      );
  }
}
